import { z } from 'zod';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { FormMultiSelect } from '@/modules/forms/components/FormMultiSelect';
import { Button } from '@/modules/ui/components/button/Button';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import type { UserApi } from '@/modules/users/types';

const userFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  meetingUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
  tenantIds: z.array(z.string()),
});

export type UserFormValues = z.infer<typeof userFormSchema>;

interface UserFormProps {
  mode: 'create' | 'update';
  initialValues?: Partial<UserApi>;
  submitText?: string;
  isPending?: boolean;
  onSubmit: (values: UserFormValues) => void;
  onCancel?: () => void;
}

export const UserForm = ({
  mode,
  initialValues,
  submitText,
  isPending,
  onSubmit,
  onCancel,
}: UserFormProps) => {
  const currentUserQuery = useCurrentUserQuery();
  const companiesQuery = useCompaniesQuery();

  const hasUserPermission = useCheckPermissions();

  const isCreateMode = mode === 'create';
  const hasUserMultipleTenants =
    companiesQuery.data?.length && companiesQuery.data.length > 1;

  const isCurrentUser = currentUserQuery.data?.id === initialValues?.id;

  const hasUserWritePermission = hasUserPermission([
    permissions.Users.Create,
    permissions.Users.Update,
  ]);

  const actionsVisible =
    isCreateMode || isCurrentUser || hasUserWritePermission;

  const defaultTenantId = currentUserQuery.data?.defaultTenantId;

  const handleFormSubmit = (values: UserFormValues) => {
    const formValues: UserFormValues = {
      ...values,
      tenantIds: hasUserMultipleTenants
        ? values.tenantIds
        : defaultTenantId
          ? [defaultTenantId]
          : [],
    };

    onSubmit(formValues);
  };

  const defaultValues = {
    firstName: initialValues?.firstName || '',
    lastName: initialValues?.lastName || '',
    email: initialValues?.email || '',
    meetingUrl: initialValues?.meetingUrl || '',
    tenantIds: initialValues?.tenantIds || [],
  };

  return (
    <Form
      defaultValues={defaultValues}
      schema={userFormSchema}
      onSubmit={handleFormSubmit}
    >
      <div className="space-y-8">
        <FormInput
          name="firstName"
          label="First Name"
          placeholder={isCreateMode ? 'John' : 'First Name'}
        />

        <FormInput
          name="lastName"
          label="Last Name"
          placeholder={isCreateMode ? 'Doe' : 'Last Name'}
        />

        {isCreateMode && (
          <>
            <FormInput
              name="email"
              label="Email"
              type="email"
              placeholder="j.doe@company.com"
              required
            />

            {hasUserMultipleTenants && (
              <PermissionsGate permissions={[permissions.Companies.Read]}>
                <CompaniesMultiSelect />
              </PermissionsGate>
            )}
          </>
        )}

        <FormInput
          name="meetingUrl"
          label="Meeting URL"
          type="url"
          placeholder="Meeting URL"
        />

        {actionsVisible && (
          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isPending}>
              {submitText || 'Save'}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
};

const CompaniesMultiSelect = () => {
  const companiesQuery = useCompaniesQuery();
  const companiesOptions = (companiesQuery.data || []).map((company) => ({
    label: company.name ?? 'Company',
    value: company.id,
  }));

  return (
    <FormMultiSelect
      name="tenantIds"
      label="Companies"
      placeholder="Select companies..."
      options={companiesOptions}
      disabled={companiesQuery.isLoading}
    />
  );
};
